import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from "react-helmet-async"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import {
  fetchAstrosites,
  fetchAstrositeStates,
  selectAstrositeByIds,
  selectAstrositesByContext,
  selectAstrositeStatesByContext,
  selectAstrositeStatesByIds,
} from "./slice"
import MapPin from "../geography/MapPin"
import AstrositeMapLabel from "./AstrositeMapLabel"
import styled from "styled-components"
import AstrositeStatus from "./AstrositeStatus"
import { selectAdvancedMode } from "../settings/slice"
import PropTypes from "prop-types"
import WindyWidget from "../weather/WindyWidget"
import PaperTitle, { TitleAction, TitleText } from "../../ui/PaperTitle"
import { Link, useNavigate } from "react-router-dom"
import { useInterval } from "../timing/use-interval"
import TelematicsLineChart from "./TelematicsLineChart"
import mpsToKmph from "../../conversions/mpsToKmph"
import { Header, Overview } from "../../ui/PageHeader"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"
import IDCopy from "../../ui/IDCopy"

const UPDATE_INTERVAL = 60000 // ms

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1rem;
  margin-bottom: 1rem;
`

/* AstrositeList renders a page displaying details about the various astrosites.*/
const AstrositeList = () => {
  const dispatch = useDispatch()

  // we get any matching Astrosites
  const astrositeMatches = useSelector((state) =>
    selectAstrositesByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const astrosites = useSelector((state) =>
    selectAstrositeByIds(state, astrositeMatches?.ids || [])
  )

  const updateData = () => {
    // We want to make sure we get results for all the astrosites even if some are offline so we must do sep calls per site.
    astrosites.forEach((site) => {
      dispatch(
        fetchAstrositeStates({
          filter: {
            astrosite_id: site?.id,
          },
          page: {
            offset: 0,
            limit: 50,
          },
          context: `astrosite/${site?.id}`,
        })
      )
    })
  }

  useEffect(() => {
    dispatch(
      fetchAstrosites({
        filter: {
          sort_by: "id",
          sort_order: "ASC",
        },
        page: { offset: 0, limit: 100 },
        context: ["root"],
      })
    ).then(() => updateData())
  }, [])

  // Poll at an interval
  useInterval(updateData, UPDATE_INTERVAL, false)

  let astrositeLocations = astrosites
    .map((astrosite) => {
      return (
        astrosite.location && {
          id: astrosite.id,
          content: <AstrositeMapLabel astrosite={astrosite} />,
          latitude: astrosite.location.latitude_degrees,
          longitude: astrosite.location.longitude_degrees,
        }
      )
    })
    .filter((location) => location)

  return (
    <Layout>
      <Helmet>
        <title>Astrosites</title>
      </Helmet>
      {astrositeMatches?._loadStatus === "loading" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      <NavMain />
      <ScrollablePage>
        <Header>
          <Overview>
            <h2>Astrosites</h2>
          </Overview>
        </Header>
        <Container>
          {astrosites.map((astrosite) => (
            <AstrositeCard astrosite={astrosite} key={astrosite?.id} />
          ))}
        </Container>
        <h4>Location of astrosites:</h4>
        {astrositeLocations.length > 0 && (
          <MapPin pins={astrositeLocations} width={"100%"} height={"50em"} />
        )}
      </ScrollablePage>
    </Layout>
  )
}

const TelemetryContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`

const WeatherContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

const Name = styled.span`
  color: ${(props) => props.color};
  font-size: 1.25rem;
  font-weight: bold;
`

const LinkContainer = styled(Link)`
  flex: none;
  display: flex;
  align-items: center;
  white-space: nowrap;
  &:link {
    text-decoration: none;
  }
`

const StatusContainer = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  white-space: nowrap;
  justify-content: space-between;
  padding-right: 1rem;
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

const ChartContainer = styled.div`
  height: 9rem;
`

const IPAddr = styled.div`
  margin-right: 1rem;
  font-size: 0.9rem;
`

const dataByName = (states = [], name) => {
  return states
    .map((state) => {
      return {
        x: new Date(state.timestamp),
        y: state[name],
      }
    })
    .filter((coord) => coord.y)
}

const AstrositeCard = ({ astrosite = {} }) => {
  // we get any matching observations for this RSO
  const advancedMode = useSelector(selectAdvancedMode)

  // we get any matching observations for this RSO
  const astrositeStateMatches = useSelector((state) =>
    selectAstrositeStatesByContext(state, `astrosite/${astrosite?.id}`)
  )

  // We get the full objects from the ids that match
  const astrositeStates = useSelector((state) =>
    selectAstrositeStatesByIds(state, astrositeStateMatches?.ids || [])
  )

  const states = astrositeStates.filter((s) => s.astrosite === astrosite?.id)

  const navigate = useNavigate()

  return (
    <Paper
      style={{ borderLeft: `3px solid ${astrosite?.color}`, cursor: "pointer" }}
      onClick={() => navigate(`/astrosites/${astrosite?.id}`)}
    >
      <PaperTitle>
        <TitleText>
          <LinkContainer to={`/astrosites/${astrosite?.id}`}>
            <Name color={astrosite?.color}>{astrosite?.name}</Name>
          </LinkContainer>
        </TitleText>
        <TitleAction>
          <IPAddr>
            <IDCopy id={astrosite?.internal_ip} showCopy />
          </IPAddr>
          <AstrositeStatus
            astrosite={astrosite}
            advancedMode={advancedMode}
            hideLastUpdated={true}
            showIcon={true}
          />
        </TitleAction>
      </PaperTitle>
      <StatusContainer></StatusContainer>

      <CardContainer>
        {astrositeStateMatches?._loadStatus !== "idle" && (
          <LoadingSpinnerInline />
        )}
        {states?.length > 0 && (
          <TelemetryContainer>
            <ChartContainer>
              <TelematicsLineChart
                data={(
                  dataByName(astrositeStates, "wind_metres_per_second") || []
                ).map((d) => ({ x: d.x, y: mpsToKmph(d.y) }))}
                title={"Wind speed (km/h)"}
                unit={"m/s"}
                xLabel={"time"}
                yLabel={"km/h"}
              />
            </ChartContainer>
            <ChartContainer>
              <TelematicsLineChart
                data={
                  dataByName(
                    astrositeStates,
                    "sky_minus_ambient_temperature_celsius"
                  ) || []
                }
                title={"Sky temp minus ambient temp (celsius)"}
                unit={"celsius"}
                xLabel={"time"}
                yLabel={"celcius"}
              />
            </ChartContainer>
            <ChartContainer>
              <TelematicsLineChart
                data={(
                  dataByName(astrositeStates, "relative_humidity") || []
                ).map((d) => ({ x: d.x, y: d.y * 100 }))}
                title={"Relative humidity"}
                unit={"percent"}
                xLabel={"time"}
                yLabel={"percent"}
              />
            </ChartContainer>
            <ChartContainer>
              <TelematicsLineChart
                data={
                  dataByName(astrositeStates, "ambient_temperature_celsius") ||
                  []
                }
                title={"Ambient temp"}
                unit={"celsius"}
                xLabel={"time"}
                yLabel={"celsius"}
              />
            </ChartContainer>
          </TelemetryContainer>
        )}
        <WeatherContainer>
          {/*<LatestAllSkyMedia astrositeId={id} these only happen once a week so are not of value yet/>*/}
          {astrosite?.location?.latitude_degrees &&
            astrosite?.location?.longitude_degrees && (
              <WindyWidget
                width={"400"}
                height={"470"}
                latitude={astrosite.location.latitude_degrees}
                longitude={astrosite.location.longitude_degrees}
              />
            )}
        </WeatherContainer>
      </CardContainer>
    </Paper>
  )
}

AstrositeCard.propTypes = {
  astrosite: PropTypes.object.isRequired,
}

export default AstrositeList
